import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Sparkles, Play, BookOpen } from 'lucide-react';
import { useState, useEffect, useCallback, memo } from 'react';
import BookingModal from '../BookingModal/BookingModal';
import './HeroSection.css';

function HeroSection() {
  const [typedText, setTypedText] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const fullText = 'Real-Time AI';

  const handleBookingModalOpen = useCallback(() => {
    setIsBookingModalOpen(true);
  }, []);

  const handleBookingModalClose = useCallback(() => {
    setIsBookingModalOpen(false);
  }, []);

  useEffect(() => {
    setIsVisible(true);
    let currentIndex = 0;
    let timeoutId: NodeJS.Timeout;

    const typeText = () => {
      if (currentIndex <= fullText.length) {
        setTypedText(fullText.slice(0, currentIndex));
        currentIndex++;
        timeoutId = setTimeout(typeText, 150);
      }
    };

    const initialTimeout = setTimeout(typeText, 1000);

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(timeoutId);
    };
  }, []);
  return (
    <section className="hero-section" role="region" aria-label="Hero section">
      {/* Floating Glow Shapes - Reduced for performance */}
      <div className="hero-section__floating-shapes" aria-hidden="true">
        <div className="floating-shape floating-shape--1"></div>
        <div className="floating-shape floating-shape--3"></div>
        <div className="floating-shape floating-shape--5"></div>
        <div className="floating-shape floating-shape--7"></div>
      </div>

      <div className="hero-section__container">
        <div className="hero-section__content">
          <Badge className={`hero-section__badge ${isVisible ? 'animate-slide-down' : ''}`}>
            <Sparkles className="w-3 h-3 mr-1" aria-hidden="true" />
            AI-Powered Learning Platform
          </Badge>

          <h1
            className={`hero-section__title ${isVisible ? 'animate-fade-up' : ''}`}
            itemProp="headline"
          >
            <span className="text-reveal">Transform Learning with</span>{' '}
            <span className="hero-section__title-gradient typing-animation">{typedText}</span>
          </h1>

          <p
            className={`hero-section__description ${isVisible ? 'animate-fade-up-delayed' : ''}`}
            itemProp="description"
          >
            Create interactive quizzes, polls, and learning experiences that adapt in real-time.
            Engage students with AI-generated content and instant feedback that makes learning
            unforgettable.
          </p>

          <div
            className={`hero-section__actions ${isVisible ? 'animate-fade-up-more-delayed' : ''}`}
          >
            <Button
              className="hero-section__secondary-btn"
              onClick={handleBookingModalOpen}
              aria-label="Book a Free Demo"
            >
              Book a Free Demo
            </Button>
            {/* <Link to="/interactive/join">
              <Button className="hero-section__tertiary-btn">
                Join a Session
                <Sparkles className="w-5 h-5 ml-2" aria-hidden="true" />
              </Button>
            </Link> */}
          </div>

          <div
            className={`hero-section__features ${isVisible ? 'animate-fade-up-most-delayed' : ''}`}
          >
            {/* <div className="hero-section__feature">
              <div className="hero-section__feature-dot hero-section__feature-dot--green glow-dot" aria-hidden="true"></div>
              <span>Live Sessions</span>
            </div> */}
            <div className="hero-section__feature">
              <div className="hero-section__feature-dot hero-section__feature-dot--blue glow-dot" aria-hidden="true"></div>
              <span>Real-time Analytics</span>
            </div>
            <div className="hero-section__feature">
              <div className="hero-section__feature-dot hero-section__feature-dot--purple glow-dot" aria-hidden="true"></div>
              <span>AI-Powered</span>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal isOpen={isBookingModalOpen} onClose={handleBookingModalClose} />

      {/* Enhanced Background Effects */}
      <div className="hero-section__background" aria-hidden="true">
        <div className="hero-section__bg-effect hero-section__bg-effect--purple-top animate-float"></div>
        <div className="hero-section__bg-effect hero-section__bg-effect--blue-right animate-float-delayed"></div>
        <div className="hero-section__bg-effect hero-section__bg-effect--purple-bottom animate-pulse-slow"></div>
      </div>
    </section>
  );
}

export default memo(HeroSection);
